import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, timer } from 'rxjs';
import { switchMap, tap, retry, shareReplay } from 'rxjs/operators';
import { environment } from 'environments/environment';

export interface AppNotification {
    id: string;
    type: 'contract' | 'booking' | 'activity' | 'info' | 'warning' | 'danger' | 'success';
    badgeClass?: string;
    title: string;
    message: string;
    action_url?: string;
    read_at?: string;
    created_at: string;
}

export interface NotificationResponse {
    success: boolean;
    data: {
        items: AppNotification[];
        unread_count: number;
    };
}

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private apiUrl = environment.baseurl_api + 'api/v1/tenant/manage/notifications';
    
    private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
    public notifications$ = this.notificationsSubject.asObservable();
    
    private unreadCountSubject = new BehaviorSubject<number>(0);
    public unreadCount$ = this.unreadCountSubject.asObservable();

    constructor(private http: HttpClient) {}

    /**
     * Start polling for notifications every minute
     */
    startPolling(): Observable<NotificationResponse> {
        return timer(0, 60000).pipe(
            switchMap(() => this.getNotifications()),
            retry(3),
            shareReplay(1)
        );
    }

    /**
     * Fetch notifications from API
     */
    getNotifications(): Observable<NotificationResponse> {
        return this.http.get<NotificationResponse>(this.apiUrl).pipe(
            tap(res => {
                if (res && res.success) {
                    this.notificationsSubject.next(res.data.items);
                    this.unreadCountSubject.next(res.data.unread_count);
                }
            })
        );
    }

    /**
     * Mark a specific notification as read
     */
    markAsRead(id: string): Observable<any> {
        return this.http.post(`${this.apiUrl}/${id}/read`, {}).pipe(
            tap(() => {
                // Update local state optimistically
                const items = this.notificationsSubject.value;
                const index = items.findIndex(n => n.id === id);
                if (index !== -1 && !items[index].read_at) {
                    items[index].read_at = new Date().toISOString();
                    this.notificationsSubject.next([...items]);
                    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
                }
            })
        );
    }

    /**
     * Mark all as read
     */
    markAllAsRead(): Observable<any> {
        return this.http.post(`${this.apiUrl}/read-all`, {}).pipe(
            tap(() => {
                // Update local state optimistically
                const items = this.notificationsSubject.value.map(n => ({
                    ...n,
                    read_at: n.read_at || new Date().toISOString()
                }));
                this.notificationsSubject.next(items);
                this.unreadCountSubject.next(0);
            })
        );
    }
}
